import React, { useEffect, useState } from 'react';
import { getDevisSansMontant } from '../services/api';

/**
 * Les devis qui attendent leur prix
 *
 * Le 19 octobre 2026 on en a trouvé 105 d'un coup, accumulés depuis des mois.
 * Huit seulement portaient vraiment un devis ; les autres n'étaient qu'une
 * case cochée par erreur. Les deux cas demandent un geste opposé, alors la
 * page les sépare au lieu de les mêler :
 *
 *   — avec devis : de l'argent à réclamer, il manque le montant ;
 *   — sans devis : du ménage, la case est à décocher.
 *
 * Tout se corrige dans Zoho. Cette page ne fait que montrer, parce qu'une
 * page qui écrit dans le CRM sans qu'on l'ait demandé est une page dont on
 * se méfie.
 */

const jour = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? String(iso) : d.toLocaleDateString('fr-FR');
};

function Tableau({ lignes, vide }) {
  if (!lignes.length) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text3)', padding: '18px 0' }}>{vide}</div>
    );
  }
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
        <thead>
          <tr style={{ background: 'var(--bg2)' }}>
            {['Commande', 'Client', 'Étape', 'Typo', 'Technicien', 'Intervention'].map(h => (
              <th key={h} style={{ padding: '7px 10px', fontSize: 10, fontWeight: 700,
                color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .3,
                textAlign: 'left', borderBottom: '1px solid var(--border)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map(l => (
            <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '6px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
                <a href={'/dossier/' + l.id} style={{ color: 'var(--blue)', textDecoration: 'none' }}>
                  {l.ref_commande || l.id}
                </a>
              </td>
              <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text2)' }}>
                {String(l.client || '').slice(0, 42)}
              </td>
              <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{l.etape}</td>
              <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text3)' }}>{l.typo}</td>
              <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{l.technicien}</td>
              <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{jour(l.date_intervention)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DevisSansMontant() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    getDevisSansMontant()
      .then(setData)
      .catch(e => setErreur(e.response?.data?.error || e.message));
  }, []);

  const avec = (data && data.avec) || [];
  const sans = (data && data.sans) || [];

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto', width: '100%',
      minWidth: 0, boxSizing: 'border-box' }}>

      <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
        Devis à compléter
      </h1>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18 }}>
        « Devis forfaitaire » coché, aucun montant saisi. Tout se corrige dans Zoho.
      </div>

      {erreur && (
        <div style={{ padding: '10px 12px', marginBottom: 12, borderRadius: 6,
          fontSize: 13, background: 'var(--bg2)', color: 'var(--accent)' }}>{erreur}</div>
      )}
      {!data && !erreur && <div style={{ color: 'var(--text3)', fontSize: 13 }}>Lecture…</div>}

      {data && (
        <>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginBottom: 2 }}>
            {avec.length} avec un devis — il manque le prix
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
            Le devis existe. Ouvrez-le, relevez le total, saisissez-le dans « Montant du
            devis forfaitaire ». Sans ce chiffre, l’intervention ne se facture pas.
          </div>
          <Tableau lignes={avec} vide="Aucun. Tous les devis portent leur montant." />

          <div style={{ height: 26 }} />

          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
            {sans.length} sans aucun devis — la case est à décocher
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
            Rien n’est attaché à ces dossiers. Une case cochée sans prix fait croire à
            une facturation qui n’existe pas : elle fausse la rémunération du technicien.
          </div>
          <Tableau lignes={sans} vide="Aucun. Rien à nettoyer." />

          <div style={{ marginTop: 18, fontSize: 11, color: 'var(--text3)' }}>
            Relevé dans Zoho le {data.releve_le ? new Date(data.releve_le).toLocaleString('fr-FR') : '—'}.
          </div>
        </>
      )}
    </div>
  );
}
